/* eslint-disable no-prototype-builtins */
import NotificationsIcon from '@mui/icons-material/Notifications';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Checkbox, CircularProgress, FormControlLabel, Grid, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { IconFileDollar } from '@tabler/icons';
import useAuth from 'hooks/useAuth';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { gridSpacing } from 'store/constant';
import MainCard from 'ui-component/cards/MainCard';
import axiosServices from 'utils/axios';
import InvoiceSettings from './InvoiceSettings';
import SubCard from 'ui-component/cards/SubCard';

const SystemSettings = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [value, setValue] = useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axiosServices.get(`/settings/email`, {
                    headers: {
                        authorization: `Bearer ${localStorage.getItem('serviceToken')}`
                    }
                });
                setSettings(data);
                setLoading(false);
            } catch (error) {
                // console.log(error);
                toast.error('Something went wrong while fetching settings.');
            }
        };
        fetchData();
    }, []);

    const handleCheckboxChange = (event) => {
        setSettings({
            ...settings,
            [event.target.name]: event.target.checked
        });
    };

    const [saveLoading, setSaveLoading] = useState(false);

    const handleSave = async () => {
        try {
            setSaveLoading(true);
            await axiosServices.patch(`/settings/email`, settings, {
                headers: {
                    authorization: `Bearer ${localStorage.getItem('serviceToken')}`
                }
            });
            setSaveLoading(false);
            toast.success('Settings updated successfully!');
        } catch (error) {
            setSaveLoading(false);
            // console.log(error);
            toast.error('Error updating settings. Please try again.');
        }
    };

    const tabsOption = [
        {
            label: ' Notifications',
            icon: <NotificationsIcon sx={{ fontSize: '1.3rem' }} />
        },
        {
            label: 'Invoice Settings',
            icon: <IconFileDollar stroke={1.5} size="20px" />
        }
    ];

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`
        };
    }

    function TabPanel({ children, value, index, ...other }) {
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && <div>{children}</div>}
            </div>
        );
    }
    const settingLabels = {
        NEW_PROPERTY: 'Send an email notification when a new property is created.',
        UPDATE_PROPERTY: 'Send an email notification when a property is updated.',
        DELETE_PROPERTY: 'Send an email notification when a property is deleted.',
        VERIFY_PROPERTY: 'Send an email notification when a property is verified.',
        PROPERTY_STATUS: 'Send an email notification when there is a change in property status.',
        WEEKLY_LISTING_PRICE_CHANGE: 'Send an email notification when there is a weekly listing price change.',
        INVOICE_MAIL: 'Send an email notification when an invoice is sent.'
    };

    return (
        <Grid container sx={{ height: '100%', justifyContent: 'center' }}>
            {user?.hasOwnProperty('id') && user?.status !== 'VERIFIED' && (
                <Grid item xs={12} mb={2}>
                    <Alert severity="warning" variant="outlined" sx={{ borderColor: 'warning.dark', backgroundColor: '#ffffff' }}>
                        <AlertTitle>Alert!</AlertTitle>
                        Your profile verification is pending. <strong>It usually takes up to 24 hours.</strong>
                    </Alert>
                </Grid>
            )}
            <MainCard sx={{ padding: '15px 24px', width: '100%' }}>
                <Grid item xs={12}>
                    <Tabs
                        value={value}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={handleChange}
                        aria-label="system settings tabs"
                        variant="scrollable"
                        sx={{
                            mb: 3,
                            '& a': {
                                minHeight: 'auto',
                                minWidth: 10,
                                py: 1.5,
                                px: 1,
                                mr: 2.25,
                                color: theme.palette.grey[600],
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center'
                            },
                            '& a.Mui-selected': {
                                color: theme.palette.primary.main
                            },
                            '& .MuiTabs-indicator': {
                                bottom: 2
                            },
                            '& a > svg': {
                                marginBottom: '0px !important',
                                mr: 1.25
                            }
                        }}
                    >
                        {tabsOption.map((tab, index) => (
                            <Tab key={index} component={Link} to="#" icon={tab.icon} label={tab.label} {...a11yProps(index)} />
                        ))}
                    </Tabs>
                    <TabPanel value={value} index={0}>
                        {loading ? (
                            <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '300px', minWidth: '90vw' }}>
                                <CircularProgress />
                            </Grid>
                        ) : (
                            <SubCard title="Send Email Notifications">
                                <Typography variant="h6" sx={{ color: 'red', mb: 5 }}>
                                    NOTE: Checked notifications will be sent to clients only.
                                </Typography>
                                <Grid container spacing={gridSpacing}>
                                    {Object.keys(settings).map((key) => (
                                        <Grid item xs={12} key={key} sx={{ pt: '15px !important' }}>
                                            <FormControlLabel
                                                control={<Checkbox checked={settings[key]} onChange={handleCheckboxChange} name={key} />}
                                                label={
                                                    settingLabels[key] ||
                                                    `Send email notification when ${key.replace(/_/g, ' ').toLowerCase()}.`
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <LoadingButton
                                    variant="contained"
                                    onClick={handleSave}
                                    loading={saveLoading}
                                    sx={{ mt: 3, width: '100%', py: 1.5 }}
                                >
                                    Save Changes
                                </LoadingButton>
                            </SubCard>
                        )}
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <InvoiceSettings />
                    </TabPanel>
                </Grid>
            </MainCard>
        </Grid>
    );
};

export default SystemSettings;
